import React from "react";
import { captureException } from "@/lib/sentry";

/**
 * Top-level React error boundary. Catches render errors that would
 * otherwise show a blank screen and renders a branded 500 page with
 * a "Take me home" CTA + a "Report bug" mailto.
 *
 * Wires into Sentry (when configured) so we know about every crash in
 * production.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    captureException(error, { componentStack: info?.componentStack });
  }

  reset = () => {
    this.setState({ error: null });
    // Hard-reload to wipe any state that triggered the error.
    if (typeof window !== "undefined") window.location.assign("/");
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen bg-[#FAFAFA] text-ink font-manrope flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center" data-testid="error-boundary-fallback">
          <div className="text-[64px] font-spectral text-crimson leading-none">500</div>
          <h1 className="font-spectral text-2xl mt-4">Something broke on our side</h1>
          <p className="text-sm text-[#6B6B6B] mt-2 leading-relaxed">
            We've logged the error and the team will look at it. You can try
            heading back to your dashboard or report what you were doing so we
            can reproduce it.
          </p>
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              type="button"
              onClick={this.reset}
              data-testid="error-boundary-home"
              className="bg-[#0E0E0E] hover:bg-[#C1272D] text-white rounded-full h-10 px-5 text-sm font-semibold transition-colors"
            >
              Take me home
            </button>
            <a
              href={`mailto:support@armstrongk9registry.com?subject=Bug%20report&body=${encodeURIComponent(
                "What I was doing:\n\n\nError: " + (this.state.error?.message || "unknown")
              )}`}
              className="rounded-full h-10 px-5 text-sm border border-[#E5E5E5] flex items-center hover:bg-white"
            >
              Report bug
            </a>
          </div>
        </div>
      </div>
    );
  }
}
